import React, { useState } from 'react';
import { Box, Dialog, Stack, Typography } from '@mui/material';

import { Button } from '../../../../components/core';
import { useDeleteWebhookMutation } from '../../../../store/modules/webhooks/api';
import { toast } from '../../../../util/toast';
import { WebhookSubscriptionInfo } from '../WebhookSubscriptionInfo';
import { WebhookEndpoint } from '../../types/types';

interface Props {
  open: boolean;
  onClose: () => void;
  webhook: WebhookEndpoint;
}

const WebhookDeleteModal = ({ open, onClose, webhook }: Props) => {
  const { subscriptionId, merchantId } = webhook;

  const [deleteWebhook] = useDeleteWebhookMutation();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteWebhook = async () => {
    setIsDeleting(true);
    await deleteWebhook({ merchantId, subscriptionId })
      .unwrap()
      .then(() => {
        onClose();
        toast.success('Webhook subscription has been deleted');
        setIsDeleting(false);
      })
      .catch(() => {
        toast.error(
          'An error occurred while deleting the webhook subscription. Please try again later.',
        );
        setIsDeleting(false);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 4,
          minWidth: { xs: '90%', md: '733px' },
          p: 3,
        },
      }}
    >
      <Typography variant="xl" fontWeight="semibold" sx={{ mb: 3 }}>
        Are you sure you want to delete this webhook subscription?
      </Typography>

      <Typography variant="large" fontWeight="semibold" sx={{ mb: 3 }}>
        This webhook subscription will be removed and no longer active upon deletion.
      </Typography>

      <Box sx={{ mb: 6 }}>
        <WebhookSubscriptionInfo webhook={webhook} />
      </Box>
      <Stack spacing={2} sx={{ '& button': { height: '48px' } }}>
        <Button
          text={isDeleting ? 'Deleting...' : 'Delete Webhook Subscription'}
          disabled={isDeleting}
          onClick={handleDeleteWebhook}
          theme="primary"
        />
        <Button text="Cancel" disabled={isDeleting} onClick={onClose} theme="secondary" />
      </Stack>
    </Dialog>
  );
};

export default WebhookDeleteModal;
